import { useEffect, useMemo, useRef } from "react";
import { EditorState, type Extension } from "@codemirror/state";
import { EditorView, basicSetup } from "codemirror";
import { json } from "@codemirror/lang-json";

/**
 * Schema-aware BO4E JSON editor: autocomplete, hover docs and lint markers come
 * from the BO4E v1 JSON schemas served by the mapper (/api/mapper/bo4e-schema).
 *
 * The catalog's relative $refs ("../enum/BOTyp.schema.json") are rewritten to
 * local "#/$defs/<file>" pointers so the editor gets ONE self-contained schema:
 * root = object whose properties mirror the produced BO4E top-level keys, each
 * resolving (case-insensitively) to its BO schema when one exists.
 */

type SchemaCatalog = Record<string, Record<string, unknown>>;

function rewriteRefs(node: unknown): unknown {
  if (Array.isArray(node)) return node.map(rewriteRefs);
  if (node && typeof node === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
      if (k === "$ref" && typeof v === "string") {
        const file = v.split("/").pop() ?? v;
        out[k] = `#/$defs/${file}`;
      } else if (k === "$id" || k === "$schema") {
        // strip — they would re-anchor resolution away from our $defs
        continue;
      } else {
        out[k] = rewriteRefs(v);
      }
    }
    return out;
  }
  return node;
}

export function buildCombinedSchema(
  catalog: SchemaCatalog,
  topLevelKeys: string[],
): Record<string, unknown> {
  const defs: Record<string, unknown> = {};
  const byLower: Record<string, string> = {};
  for (const [name, schema] of Object.entries(catalog)) {
    const src = (schema as { $__source?: string }).$__source ?? `${name}.schema.json`;
    const file = src.split("/").pop() ?? src;
    defs[file] = rewriteRefs(schema);
    byLower[name.toLowerCase()] = file;
  }
  const properties: Record<string, unknown> = {};
  for (const key of topLevelKeys) {
    const file = byLower[key.toLowerCase()];
    properties[key] = file ? { $ref: `#/$defs/${file}` } : { type: "object" };
  }
  return {
    type: "object",
    properties,
    additionalProperties: true,
    $defs: defs,
  };
}

interface Props {
  value: string;
  onChange?: (text: string) => void;
  schemaCatalog: SchemaCatalog | null;
  /** top-level keys of the BO4E result — drive which BO schemas anchor the root */
  topLevelKeys: string[];
  height?: string;
}

export function Bo4eJsonEditor({ value, onChange, schemaCatalog, topLevelKeys, height = "480px" }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  // Mount ist async (dynamic import) — der Editor muss den dann-aktuellen Wert
  // bekommen, nicht den Closure-Wert vom Effekt-Start.
  const valueRef = useRef(value);
  valueRef.current = value;

  const schema = useMemo(
    () => (schemaCatalog ? buildCombinedSchema(schemaCatalog, topLevelKeys) : null),
    [schemaCatalog, topLevelKeys],
  );

  useEffect(() => {
    if (!host.current) return;
    let view: EditorView | null = null;
    let cancelled = false;

    async function mount() {
      const extensions: Extension[] = [
        basicSetup,
        json(),
        EditorView.theme({
          "&": { height, fontSize: "0.85rem" },
          ".cm-scroller": { fontFamily: "var(--sapFontMonoFamily, monospace)" },
        }),
        EditorView.updateListener.of((u) => {
          if (u.docChanged && onChange) onChange(u.state.doc.toString());
        }),
      ];
      if (schema) {
        try {
          // Dynamisch geladen: liefert completion + hover-doku + lint aus dem
          // Schema. Bei Lade-Fehlern degradiert der Editor zu plain JSON.
          const { jsonSchema } = await import("codemirror-json-schema");
          extensions.push(jsonSchema(schema as never));
        } catch {
          /* plain JSON editor */
        }
      }
      if (cancelled || !host.current) return;
      // Eigener ShadowRoot: CodeMirror mountet seine Styles via adoptedStyleSheets,
      // und UI5 überschreibt document.adoptedStyleSheets beim Theme-Setup — im
      // Shadow-Scope bleiben die CM-Styles unangetastet.
      const shadow = host.current.shadowRoot ?? host.current.attachShadow({ mode: "open" });
      shadow.replaceChildren();
      view = new EditorView({
        state: EditorState.create({ doc: valueRef.current, extensions }),
        parent: shadow,
        root: shadow,
      });
      viewRef.current = view;
    }

    void mount();
    return () => {
      cancelled = true;
      view?.destroy();
      if (viewRef.current === view) viewRef.current = null;
    };
    // Recreate the editor when the schema identity changes (new apply result);
    // `value` changes are pushed via the effect below to keep cursor state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, height]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current !== value) {
      view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
    }
  }, [value]);

  return <div ref={host} style={{ border: "1px solid var(--sapField_BorderColor, #89919a)", borderRadius: "4px", overflow: "hidden" }} />;
}
